import { useTranslation } from "react-i18next"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { ArrowsClockwise } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { Tag } from "@/components/ui/tag"
import { BetterDocField } from "@/modules/admin/better-doc/components/better-doc-field"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocDateTime } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { useBetterDocHealthSummaryQuery } from "@/modules/admin/better-doc/services/__generated__/useBetterDocHealthSummaryQuery.graphql"
import { betterDocHealthSummaryQuery } from "@/modules/admin/better-doc/services/useBetterDocHealthSummaryQuery"

type BetterDocHealthSummaryProps = {
  queryRef: PreloadedQuery<useBetterDocHealthSummaryQuery>
  onRefresh: () => void
}

export const BetterDocHealthSummary = ({ queryRef, onRefresh }: BetterDocHealthSummaryProps) => {
  const { t } = useTranslation()
  const { de_betterDocHealthSummary: healthSummary } = usePreloadedQuery<useBetterDocHealthSummaryQuery>(
    betterDocHealthSummaryQuery,
    queryRef,
  )
  const { lastSuccessfulExchangeAt, failedExchangeCountLast24Hours, stuckSessions } = healthSummary

  return (
    <Card data-testid="better-doc-admin-health-summary">
      <CardHeader>
        <CardHeaderTitle>{t("admin.betterDoc.healthSummary.title")}</CardHeaderTitle>
        <Button
          type="button"
          variant="tertiary"
          className="p-2"
          aria-label={t("common.button.refresh")}
          onClick={onRefresh}
          data-testid="better-doc-admin-health-refresh-button">
          <ArrowsClockwise className="size-5" />
        </Button>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <div className="grid grid-cols-3 gap-4">
          <BetterDocField
            label={t("admin.betterDoc.healthSummary.lastSuccessfulExchange")}
            value={
              lastSuccessfulExchangeAt === null
                ? t("admin.betterDoc.healthSummary.noSuccessfulExchange")
                : formatBetterDocDateTime(lastSuccessfulExchangeAt)
            }
            testId="better-doc-admin-health-last-success"
          />
          <BetterDocField
            label={t("admin.betterDoc.healthSummary.failedExchanges")}
            value={
              <Tag
                type={failedExchangeCountLast24Hours > 0 ? "critical" : "success"}
                label={String(failedExchangeCountLast24Hours)}
              />
            }
            testId="better-doc-admin-health-failed-count"
          />
          <BetterDocField
            label={t("admin.betterDoc.healthSummary.stuckSessionCount")}
            value={<Tag type={stuckSessions.length > 0 ? "warning" : "success"} label={String(stuckSessions.length)} />}
            testId="better-doc-admin-health-stuck-count"
          />
        </div>
        {stuckSessions.length === 0 ? (
          <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-health-stuck-empty">
            {t("admin.betterDoc.healthSummary.noStuckSessions")}
          </p>
        ) : (
          <section className="flex flex-col gap-2">
            <h3 className="text-on-surface text-sm font-medium">{t("admin.betterDoc.healthSummary.stuckSessions")}</h3>
            <BetterDocTable data-testid="better-doc-admin-health-stuck-table">
              <thead>
                <tr>
                  <BetterDocTableHeaderCell>{t("admin.betterDoc.healthSummary.session")}</BetterDocTableHeaderCell>
                  <BetterDocTableHeaderCell>
                    {t("admin.betterDoc.healthSummary.partnerSessionId")}
                  </BetterDocTableHeaderCell>
                  <BetterDocTableHeaderCell>{t("admin.betterDoc.healthSummary.kind")}</BetterDocTableHeaderCell>
                  <BetterDocTableHeaderCell>{t("admin.betterDoc.healthSummary.waitingSince")}</BetterDocTableHeaderCell>
                </tr>
              </thead>
              <tbody>
                {stuckSessions.map((stuckSession) => (
                  <tr
                    key={stuckSession.session.sessionId}
                    className="hover:bg-surface-hovered"
                    data-testid="better-doc-admin-health-stuck-row">
                    <BetterDocTableCell>
                      <BetterDocSessionLink sessionId={stuckSession.session.sessionId} />
                    </BetterDocTableCell>
                    <BetterDocTableCell className="break-all">
                      {stuckSession.session.partnerSessionId}
                    </BetterDocTableCell>
                    <BetterDocTableCell>
                      {stuckSession.session.isTestCase ? (
                        <div className="flex flex-wrap items-center gap-1">
                          <Tag type="attention" hasIcon={false} label={t("admin.betterDoc.testCaseTag")} />
                          {stuckSession.session.testCaseScenario === null ? null : (
                            <span>
                              {getBetterDocEnumLabel(t, "testCaseScenario", stuckSession.session.testCaseScenario)}
                            </span>
                          )}
                        </div>
                      ) : (
                        t("admin.betterDoc.healthSummary.realSession")
                      )}
                    </BetterDocTableCell>
                    <BetterDocTableCell className="whitespace-nowrap">
                      {formatBetterDocDateTime(stuckSession.waitingSince)}
                    </BetterDocTableCell>
                  </tr>
                ))}
              </tbody>
            </BetterDocTable>
          </section>
        )}
      </CardBody>
    </Card>
  )
}
